import MEGA_CLIENT from './client'

// Terminal Block Assignment interface
export interface TerminalBlockAssignment {
  id: number
  terminal_block_id: number
  device_instance_id: number
  position_in_block: number
  properties: Record<string, any> | null
  created_at: string
  // Related data from JOINs
  position_label?: string
  description?: string
  specification_name?: string
  specification_model?: string
}

// Get terminal block assignments
export const getTerminalBlockAssignments = async (terminalBlockId: number): Promise<TerminalBlockAssignment[]> => {
  try {
    const response = await MEGA_CLIENT.get(`/api/terminal-block-assignments?terminal_block_id=${terminalBlockId}`)
    return response.data || []
  } catch (error) {
    console.error('❌ Error loading terminal block assignments:', error);
    throw error;
  }
}

// Get single assignment
export const getTerminalBlockAssignment = async (id: number): Promise<TerminalBlockAssignment> => {
  const response = await MEGA_CLIENT.get(`/api/terminal-block-assignments/${id}`)
  return response.data as TerminalBlockAssignment
}

// Create assignment
export const createTerminalBlockAssignment = async (assignment: {
  terminal_block_id: number
  device_instance_id: number
  position_in_block: number
  properties?: Record<string, any>
}): Promise<TerminalBlockAssignment> => {
  const response = await MEGA_CLIENT.post('/api/terminal-block-assignments', assignment)
  return response.data as TerminalBlockAssignment
}

// Update assignment
export const updateTerminalBlockAssignment = async (id: number, updates: {
  device_instance_id?: number
  position_in_block?: number
  properties?: Record<string, any>
}): Promise<TerminalBlockAssignment> => {
  const response = await MEGA_CLIENT.put(`/api/terminal-block-assignments/${id}`, updates)
  return response.data as TerminalBlockAssignment
}

// Delete assignment
export const deleteTerminalBlockAssignment = async (id: number): Promise<void> => {
  await MEGA_CLIENT.delete(`/api/terminal-block-assignments/${id}`)
}

// Device Instance interface for terminal assignment
export interface DeviceInstance {
  id: number
  specification_id: number
  position_label: string | null
  description: string | null
  spec_name?: string
  device_type?: string
}

// Terminal Specification interface
export interface TerminalSpecification {
  id: number
  name: string
  device_type: string
  model?: string
  electrical_panel_id: number
}

// Get available terminal specifications for creating new instances
export const getAvailableTerminalSpecifications = async (projectId: number): Promise<TerminalSpecification[]> => {
  const response = await MEGA_CLIENT.get(`/api/specification?project_id=${projectId}&device_types=terminal`)
  return response.data || []
}

// Get next available position in terminal block
export const getNextPosition = async (terminalBlockId: number): Promise<number> => {
  const assignments = await getTerminalBlockAssignments(terminalBlockId)
  const positions = assignments.map(a => a.position_in_block)
  let nextPosition = 1
  while (positions.includes(nextPosition)) {
    nextPosition++
  }
  return nextPosition
}

// Create new device instance from specification
export const createDeviceInstanceFromSpecification = async (specificationId: number, electricalPanelId: number, projectId: number): Promise<DeviceInstance> => {
  const response = await MEGA_CLIENT.post('/api/device-instances', {
    specification_id: specificationId,
    electrical_panel_id: electricalPanelId,
    project_id: projectId
  })
  return response.data
}

// Update device instance (position_label and description)
export const updateDeviceInstance = async (id: number, updates: {
  position_label?: string
  description?: string
}): Promise<void> => {
  await MEGA_CLIENT.put(`/api/device-instances/${id}`, updates)
}

// Batch update positions for terminal block assignments
export const batchUpdatePositions = async (updates: Array<{
  id: number
  position_in_block: number
}>): Promise<void> => {
  await MEGA_CLIENT.put('/api/terminal-block-assignments/batch-positions', { updates })
}

// Create internal connections for terminal levels
export const createTerminalInternalConnections = async (specificationId: number, levelCount: number): Promise<void> => {
  try {
    // Get contacts for this specification
    const contactsResponse = await MEGA_CLIENT.get(`/api/device-contacts?specification_id=${specificationId}`);
    const contacts = contactsResponse.data || [];
    
    // Create internal connections for each level
    for (let level = 1; level <= levelCount; level++) {
      const upperContact = contacts.find((c: any) => c.contact_label === `${level}U`);
      const lowerContact = contacts.find((c: any) => c.contact_label === `${level}D`);
      
      if (upperContact && lowerContact) {
        await MEGA_CLIENT.post('/api/terminal-internal-connections', {
          specification_id: specificationId,
          contact_from_id: upperContact.id,
          contact_to_id: lowerContact.id,
          level_number: level,
          connection_type: 'internal',
          description: `${level}U - ${level}D connection (level ${level})`
        });
      }
    }
  } catch (error) {
    console.error('Error creating terminal internal connections:', error);
    throw error;
  }
}; 